<script setup lang="ts">
import { error as logError } from "@/utils/debug"
import { ref, computed, watch } from "vue"
import {
  NDrawer,
  NDrawerContent,
  NFlex,
  NText,
  NAvatar,
  NSpin,
  NEmpty,
  NIcon,
  NButton,
  NBadge,
  useMessage,
} from "naive-ui"
import {
  NotificationsOutline,
  PersonAddOutline,
  CheckmarkCircleOutline,
  PersonOutline,
  CloseCircleOutline,
  ChatbubbleOutline,
  HeartOutline,
} from "@vicons/ionicons5"
import { useNotifications } from "@/composables/notification/useNotifications"
import { useFollowing } from "@/composables/user/useFollowing"
import { useAuth } from "@/composables/auth/useAuth"
import { useRouter } from "vue-router"
import { formatDistanceToNow } from "date-fns"
import { groupNotifications, type GroupedNotification } from "@/utils/notificationGrouping"

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<(e: "update:show", value: boolean) => void>()

const message = useMessage()
const router = useRouter()
const { user } = useAuth()
const {
  notifications,
  isLoading,
  unreadCount,
  fetchNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
} = useNotifications()

const { respondToFollowRequest } = useFollowing()
const processingRequests = ref<Set<string>>(new Set())

const showDrawer = computed({
  get: () => props.show,
  set: (value) => emit("update:show", value),
})

function onClose() {
  emit("update:show", false)
}

// Group and prioritize notifications
const prioritizedNotifications = computed(() => {
  // First, group the notifications
  const grouped = groupNotifications(notifications.value)

  // Then apply priority sorting
  const priorityMap: Record<string, number> = {
    follow_request_received: 1,
    follow_request: 1,
    comment_reply: 2,
    activity_comment: 3,
    activity_like: 4,
    follow_request_accepted: 5,
    new_follower: 6,
  }

  return grouped.sort((a, b) => {
    // First sort by priority
    const priorityA = priorityMap[a.type] || 99
    const priorityB = priorityMap[b.type] || 99
    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    // Then by unread status
    if (a.read !== b.read) {
      return a.read ? 1 : -1
    }

    // Finally by date (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  })
})

// Only fetch when drawer is opened (not on mount)
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      fetchNotifications()
    }
  },
)

function getNotificationIcon(type: string) {
  switch (type) {
    case "follow_request_received":
    case "follow_request":
      return PersonAddOutline
    case "follow_request_accepted":
      return CheckmarkCircleOutline
    case "new_follower":
      return PersonOutline
    case "comment_reply":
    case "activity_comment":
      return ChatbubbleOutline
    case "activity_like":
      return HeartOutline
    default:
      return NotificationsOutline
  }
}

function getTimeAgo(date: string): string {
  return formatDistanceToNow(new Date(date), { addSuffix: true })
}

function isFollowRequest(notification: GroupedNotification): boolean {
  return notification.type === "follow_request_received" || notification.type === "follow_request"
}

async function handleFollowRequest(
  notification: GroupedNotification,
  action: "accept" | "reject",
  event: Event,
) {
  event.stopPropagation()

  // For follow requests, actor_id is the follower_id
  if (!notification.actor_id) {
    message.error("Invalid follow request")
    return
  }

  processingRequests.value.add(notification.id)

  try {
    const success = await respondToFollowRequest(notification.actor_id, action)

    if (!success) {
      // If the request doesn't exist, just remove the notification
      message.warning("This follow request is no longer available")
      await deleteNotification(notification.id)
      // Refetch to update the UI
      await fetchNotifications()
      return
    }

    message.success(`Follow request ${action}ed`)

    // Mark as read and delete the notification
    await markAsRead(notification.id)
    await deleteNotification(notification.id)
    // Refetch to update the UI
    await fetchNotifications()
  } catch (err) {
    logError("Error handling follow request:", err)
    // Remove stale notification on error
    await deleteNotification(notification.id)
    message.error("This follow request has expired or was already handled")
    // Refetch to update the UI
    await fetchNotifications()
  } finally {
    processingRequests.value.delete(notification.id)
  }
}

async function handleNotificationClick(notification: GroupedNotification) {
  // Don't navigate if it's a follow request (has action buttons)
  if (isFollowRequest(notification)) {
    return
  }

  // Mark as read
  if (!notification.read) {
    await markAsRead(notification.id)
  }

  // Navigate based on type and available data
  if (notification.type === "activity_like") {
    // Navigate to profile with activity ID in query for highlighting
    if (user.value?.id && notification.reference_id) {
      router.push({
        name: "Profile",
        query: { highlight: notification.reference_id, action: "like" },
      })
      showDrawer.value = false
    }
  } else if (notification.type === "activity_comment" || notification.type === "comment_reply") {
    // Navigate to profile with activity ID and open comments
    if (user.value?.id && notification.reference_id) {
      router.push({
        name: "Profile",
        query: {
          highlight: notification.reference_id,
          action: "comment",
          // For comment replies, pass the comment ID
          commentId:
            notification.type === "comment_reply" ? notification.metadata?.comment_id : undefined,
        },
      })
      showDrawer.value = false
    }
  } else if (notification.actor_username) {
    // For other types (like follows), navigate to the actor's profile
    router.push({
      name: "UserProfile",
      params: { username: notification.actor_username },
    })
    showDrawer.value = false
  }
}

async function handleDelete(notificationId: string, event: Event) {
  event.stopPropagation()
  await deleteNotification(notificationId)
}

async function handleMarkAllRead() {
  await markAllAsRead()
}
</script>

<template>
  <n-drawer v-model:show="showDrawer" :width="400" placement="right">
    <n-drawer-content closable>
      <template #header>
        <n-flex justify="space-between" align="center" style="width: 100%">
          <n-text strong style="font-size: 18px">Notifications</n-text>
          <n-button
            v-if="unreadCount > 0"
            text
            size="small"
            @click="handleMarkAllRead">
            Mark all read
          </n-button>
        </n-flex>
      </template>

      <n-spin :show="isLoading">
        <!-- Notifications List -->
        <n-flex v-if="prioritizedNotifications.length > 0" vertical :size="8">
          <div
            v-for="notification in prioritizedNotifications"
            :key="notification.id"
            class="notification-item"
            :class="{
              unread: !notification.read,
              clickable: !isFollowRequest(notification),
            }"
            @click="handleNotificationClick(notification)"
            :role="isFollowRequest(notification) ? 'article' : 'button'"
            :tabindex="isFollowRequest(notification) ? -1 : 0"
            @keydown.enter="handleNotificationClick(notification)"
            @keydown.space.prevent="handleNotificationClick(notification)">
            <n-flex vertical :size="8">
              <n-flex justify="space-between" align="start" :wrap="false">
                <n-flex
                  align="center"
                  :size="12"
                  :wrap="false"
                  style="flex: 1; min-width: 0">
                  <!-- Actor Avatar or Icon -->
                  <div class="notification-avatar">
                    <n-avatar
                      v-if="notification.actor_profile_pic_url"
                      round
                      :size="40"
                      :src="notification.actor_profile_pic_url"
                      :fallback-src="`https://api.dicebear.com/7.x/initials/svg?seed=${notification.actor_username}`" />
                    <n-icon
                      v-else
                      :size="40"
                      :component="getNotificationIcon(notification.type)"
                      :color="
                        notification.read
                          ? 'var(--n-text-color-disabled)'
                          : 'var(--color-warning)'
                      " />
                  </div>

                  <!-- Notification Content -->
                  <n-flex vertical :size="4" style="min-width: 0; flex: 1">
                    <n-text
                      :strong="!notification.read"
                      :depth="notification.read ? 3 : undefined"
                      style="
                        font-size: 14px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                      ">
                      {{ notification.message }}
                    </n-text>
                    <n-text depth="3" style="font-size: 12px">
                      {{ getTimeAgo(notification.created_at) }}
                    </n-text>
                  </n-flex>
                </n-flex>

                <!-- Actions -->
                <n-flex :size="4" align="center">
                  <n-badge v-if="!notification.read" dot />
                  <n-button
                    text
                    circle
                    size="small"
                    @click="(e) => handleDelete(notification.id, e)"
                    aria-label="Delete notification">
                    <template #icon>
                      <n-icon :size="16" :component="CloseCircleOutline" />
                    </template>
                  </n-button>
                </n-flex>
              </n-flex>

              <!-- Follow Request Actions -->
              <n-flex
                v-if="isFollowRequest(notification)"
                :size="8"
                justify="end"
                style="margin-top: 4px">
                <n-button
                  size="small"
                  type="error"
                  secondary
                  :loading="processingRequests.has(notification.id)"
                  :disabled="processingRequests.has(notification.id)"
                  @click="
                    (e) => handleFollowRequest(notification, 'reject', e)
                  ">
                  Reject
                </n-button>
                <n-button
                  size="small"
                  type="primary"
                  :loading="processingRequests.has(notification.id)"
                  :disabled="processingRequests.has(notification.id)"
                  @click="
                    (e) => handleFollowRequest(notification, 'accept', e)
                  ">
                  Accept
                </n-button>
              </n-flex>
            </n-flex>
          </div>
        </n-flex>

        <!-- Empty State -->
        <n-empty
          v-else
          description="No notifications yet"
          style="padding: 60px 0">
          <template #icon>
            <n-icon :size="48" :component="NotificationsOutline" depth="3" />
          </template>
        </n-empty>
      </n-spin>

      <template #footer>
        <button
          class="icon-text"
          @click="onClose"
          title="Close add books dialog"
          aria-label="Close add books dialog"
          type="button">
          <n-icon size="30" aria-hidden="true">
            <CloseCircleOutline />
          </n-icon>
        </button>
      </template>
    </n-drawer-content>
  </n-drawer>
</template>

<style scoped>
.notification-item {
  padding: 12px;
  border-radius: 8px;
  transition: background-color 0.2s;
  border: 1px solid transparent;
}

.notification-item.clickable {
  cursor: pointer;
}

.notification-item.clickable:hover {
  background-color: var(--n-color-hover);
}

.notification-item.unread {
  background-color: color-mix(in srgb, var(--color-warning), transparent 95%);
  border-color: color-mix(in srgb, var(--color-warning), transparent 80%);
}

.notification-avatar {
  flex-shrink: 0;
}
</style>
